import React, { ReactNode } from 'react';
import { useCapabilities } from '@hooks/useCapabilities';

// Props for the WithCapability component
interface WithCapabilityProps {
  capability?: string;
  capabilities?: string[];
  requireAll?: boolean; // If true, user must have ALL capabilities; if false, user must have ANY capability
  fallback?: ReactNode; // What to render if the user lacks the required capability
  children: ReactNode;
}

/**
 * Higher-order component for conditional rendering based on user capabilities
 *
 * @example
 * // Show button only if user can create offers
 * <WithCapability capability="offers.create">
 *   <Button>Create Offer</Button>
 * </WithCapability>
 *
 * @example
 * // Show settings if user can view OR update company settings
 * <WithCapability capabilities={['company_settings.view', 'company_settings.update']}>
 *   <CompanySettings />
 * </WithCapability>
 *
 * @example
 * // Show advanced features only if user has ALL specified capabilities
 * <WithCapability capabilities={['offers.delete', 'company_settings.manage_users']} requireAll>
 *   <AdvancedFeatures />
 * </WithCapability>
 *
 * @example
 * // Show fallback content if user doesn't have permission
 * <WithCapability capability="offers.delete" fallback={<div>You don't have permission to delete offers</div>}>
 *   <DeleteButton />
 * </WithCapability>
 */
export const WithCapability: React.FC<WithCapabilityProps> = ({
  capability,
  capabilities,
  requireAll = false,
  fallback = null,
  children,
}) => {
  const { hasCapability, hasAnyCapability, hasAllCapabilities } = useCapabilities();

  let hasPermission = true;
  if (capability) {
    hasPermission = hasCapability(capability);
  } else if (capabilities && capabilities.length > 0) {
    hasPermission = requireAll ? hasAllCapabilities(capabilities) : hasAnyCapability(capabilities);
  }

  return hasPermission ? <>{children}</> : <>{fallback}</>;
};
